import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Input, Space, Table, Tag, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, ExportOutlined, ImportOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  apiErrorMessage,
  COMPETENCY_PERMS as P,
  competencyKeys,
  useCompetencyCan,
  useJobProfiles,
} from '@/hooks/useCompetency';
import { competencyService, type JobProfile } from '@/services/competency.service';
import { triggerDownload } from '@/reports/utils/exportTable';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { COMPETENCY_BASE } from './competencyDisplay';

const JobProfilesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = useCompetencyCan(P.jobProfileManage);
  const canImport = useCompetencyCan(P.import);
  const canView = useCompetencyCan(P.view);
  const canExport = canImport || canManage || canView;
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<'template' | 'export' | 'import' | null>(null);
  const { data = [], isLoading, refetch } = useJobProfiles(search ? { search } : undefined);

  const columns: ColumnsType<JobProfile> = [
    {
      title: 'Code',
      dataIndex: 'code',
      width: 140,
      render: (v, r) => <a onClick={() => navigate(`${COMPETENCY_BASE}/job-profiles/${r.id}`)}>{v}</a>,
    },
    { title: 'Job position', dataIndex: 'title' },
    { title: 'Department', dataIndex: 'department_name' },
    { title: 'Requirements', dataIndex: 'requirement_count', width: 120, align: 'right' },
    { title: 'Employees', dataIndex: 'employee_count', width: 110, align: 'right' },
    { title: 'Effective', dataIndex: 'effective_date', width: 120, render: (v) => formatDate(v) || '-' },
    {
      title: 'Status',
      dataIndex: 'is_active',
      width: 100,
      render: (v) => (Number(v) ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>),
    },
  ];

  const downloadTemplate = async () => {
    setBusy('template');
    try {
      triggerDownload(
        await competencyService.downloadImportTemplate('job-profiles'),
        'job-descriptions-template.xlsx'
      );
      message.success('Template downloaded');
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const exportCurrent = async () => {
    setBusy('export');
    try {
      triggerDownload(await competencyService.exportJobProfiles(), 'job-descriptions-export.xlsx');
      message.success('Export started');
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const importFile = async (file: File) => {
    setBusy('import');
    try {
      const check = await competencyService.runImport('job-profiles', file, true);
      if (check.errors.length) {
        message.error(`Check failed: ${check.errors[0].message}${check.errors.length > 1 ? ` (+${check.errors.length - 1} more)` : ''}`);
        navigate(`${COMPETENCY_BASE}/import?kind=job-profiles`);
        return false;
      }
      const result = await competencyService.runImport('job-profiles', file, false);
      message.success(`Imported: ${result.created} created, ${result.updated} updated`);
      queryClient.invalidateQueries({ queryKey: competencyKeys.all });
      void refetch();
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
    return false;
  };

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title="Job descriptions"
        extra={
          <Space wrap>
            {canExport ? (
              <Button icon={<ExportOutlined />} loading={busy === 'export'} onClick={() => void exportCurrent()}>
                Export
              </Button>
            ) : null}
            {canImport ? (
              <>
                <Button icon={<DownloadOutlined />} loading={busy === 'template'} onClick={() => void downloadTemplate()}>
                  Template
                </Button>
                <Upload
                  accept=".xlsx,.xls"
                  showUploadList={false}
                  beforeUpload={(f) => {
                    void importFile(f);
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />} loading={busy === 'import'}>
                    Import
                  </Button>
                </Upload>
                <Link to={`${COMPETENCY_BASE}/import?kind=job-profiles`}>
                  <Button icon={<ImportOutlined />}>Bulk import wizard</Button>
                </Link>
              </>
            ) : null}
            {canManage ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`${COMPETENCY_BASE}/job-profiles/new`)}>
                New job description
              </Button>
            ) : null}
          </Space>
        }
      />
      <Card>
        <Input.Search
          placeholder="Search code or title"
          allowClear
          style={{ width: 300, marginBottom: 12 }}
          onSearch={setSearch}
        />
        <Table rowKey="id" size="small" loading={isLoading} dataSource={data} columns={columns} pagination={{ defaultPageSize: 20 }} />
      </Card>
    </div>
  );
};

export default JobProfilesPage;
